// ANN-1/2 + TL-6: Text, Label, Freehand, and Tape tools (small one-shot state machines).
import { dist, type Vec } from "../core/geometry";
import { formatLength } from "../core/units";
import { simplifyRDP } from "../core/simplify";
import { newId, defaultTextStyle } from "../doc/document";

/**
 * The size of NEW text and labels, drawing mm (owner, 2026-10-20: "the default text size is too small, it needs to be
 * like 10-15 at least; it currently defaults to 3.5"). 3.5 is a paper size; a pool plan is drawn at full size, so 3.5 mm
 * text cannot be read at a normal zoom. Pump labels, grid labels, dimensions and DXF import keep their own sizes.
 */
export const NEW_TEXT_SIZE_MM = 15;
const newTextStyle = () => ({ ...defaultTextStyle(), size: NEW_TEXT_SIZE_MM });
import type { LabelEntity, PolylineEntity, TextEntity } from "../doc/entities";
import type { RenderView } from "../engine/renderEntities";
import { strokeWidthWorld } from "../engine/renderEntities";
import { drawReadout } from "./line";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

abstract class BaseTool implements Tool {
  abstract readonly id: Tool["id"];
  constructor(protected ctx: ToolCtx) {}
  activate(): void {}
  deactivate(): void {
    this.cancel();
  }
  cancel(): void {}
  inProgress(): boolean {
    return false;
  }
  pointerDown(_e: ToolPointerEvent): void {}
  pointerMove(_e: ToolPointerEvent): void {}
  pointerUp(_e: ToolPointerEvent): void {}
  doubleClick(_e: ToolPointerEvent): void {}
  key(_e: ToolKeyEvent): boolean {
    return false;
  }
  measurement(_raw: string): boolean {
    return false;
  }
  renderDynamic?(v: RenderView): void;
}

export class TextTool extends BaseTool {
  readonly id = "text" as const;

  override activate(): void {
    this.ctx.setMeasure("none", "");
    this.ctx.setHint("Text: click where the text should start");
  }

  override pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    const point = e.point;
    const ts = newTextStyle();
    this.ctx.requestTextEdit({
      world: point,
      fontMm: ts.size,
      initial: "",
      multiline: true,
      onCommit: (text) => {
        if (text.trim().length > 0) {
          // EC-TL-14: empty text creates nothing
          const entity: TextEntity = {
            id: newId(),
            type: "text",
            layerId: this.ctx.activeLayerId(),
            locked: false,
            hidden: false,
            pos: point,
            rotationDeg: 0,
            content: text,
            textStyle: ts
          };
          this.ctx.store.dispatch({
            kind: "addEntities",
            entities: [entity],
            z: [{ layerId: entity.layerId, index: -1 }]
          });
          this.ctx.selection.set([entity.id]);
        }
        this.ctx.switchTool("select"); // one-shot (TL-6) — only once the edit closes
      },
      onCancel: () => this.activate() // Esc cancels the edit, not the tool
    });
  }
}

export class LabelTool extends BaseTool {
  readonly id = "label" as const;
  private head: Vec | null = null;
  private cursor: Vec | null = null;

  override activate(): void {
    this.head = null;
    this.ctx.setMeasure("none", "");
    this.ctx.setHint("Label: click the spot to point at (the arrow head)");
  }

  override cancel(): void {
    this.head = null;
    this.ctx.setSnapBase(null);
    this.ctx.setCursorTip(null);
  }

  override inProgress(): boolean {
    return this.head !== null;
  }

  override pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    if (!this.head) {
      this.head = e.point;
      this.ctx.setSnapBase(e.point);
      this.ctx.setHint("Label: click where the text should sit");
      this.ctx.setCursorTip("text position");
      return;
    }
    const head = this.head;
    const tail = e.point;
    if (dist(head, tail) < 1e-9) return; // EC-TL-5
    const ts = newTextStyle();
    this.ctx.requestTextEdit({
      world: tail,
      fontMm: ts.size,
      initial: "",
      multiline: false,
      onCommit: (text) => {
        if (text.trim().length > 0) {
          // EC-TL-14: empty text creates nothing
          const doc = this.ctx.store.doc;
          const entity: LabelEntity = {
            id: newId(),
            type: "label",
            layerId: this.ctx.activeLayerId(),
            locked: false,
            hidden: false,
            head,
            tail,
            content: text,
            textStyle: ts,
            arrow: "arrow",
            style: structuredClone(doc.defaultStyle)
          };
          this.ctx.store.dispatch({
            kind: "addEntities",
            entities: [entity],
            z: [{ layerId: entity.layerId, index: -1 }]
          });
          this.ctx.selection.set([entity.id]);
        }
        this.ctx.switchTool("select"); // one-shot — only once the edit closes
      },
      onCancel: () => this.activate() // Esc cancels the edit; place the label again
    });
    this.head = null;
    this.ctx.setSnapBase(null);
  }

  override pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    this.ctx.requestRender();
  }

  override key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape" && this.head) {
      this.cancel();
      this.activate();
      this.ctx.requestRender();
      return true;
    }
    return false;
  }

  override renderDynamic(v: RenderView): void {
    if (!this.head || !this.cursor) return;
    const { ctx } = v;
    ctx.strokeStyle = this.ctx.store.doc.defaultStyle.strokeColor;
    ctx.lineWidth = strokeWidthWorld(this.ctx.store.doc.defaultStyle, v.cam, v.dpr);
    ctx.setLineDash([2 / v.cam.scale, 2 / v.cam.scale]);
    ctx.beginPath();
    ctx.moveTo(this.head.x, this.head.y);
    ctx.lineTo(this.cursor.x, this.cursor.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }
}

export class FreehandTool extends BaseTool {
  readonly id = "freehand" as const;
  private points: Vec[] = [];
  private capturing = false;

  override activate(): void {
    this.points = [];
    this.capturing = false;
    this.ctx.setMeasure("none", "");
    this.ctx.setHint("Freehand: press and drag to draw · release to finish");
  }

  override cancel(): void {
    this.points = [];
    this.capturing = false;
    this.ctx.gestureActive(false);
  }

  override inProgress(): boolean {
    return this.capturing;
  }

  override pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    this.capturing = true;
    this.points = [e.world]; // raw cursor — snapping fights sketching
    this.ctx.gestureActive(true);
  }

  override pointerMove(e: ToolPointerEvent): void {
    if (!this.capturing) return;
    const last = this.points[this.points.length - 1]!;
    if (dist(e.world, last) * this.ctx.scale() >= 2) {
      this.points.push(e.world); // ≥ 2 px spacing (TL-6)
      this.ctx.requestRender();
    }
  }

  override pointerUp(): void {
    if (!this.capturing) return;
    this.capturing = false;
    this.ctx.gestureActive(false);
    const simplified = simplifyRDP(this.points, this.ctx.prefs().freehandTolPx / this.ctx.scale()); // TL-6 RDP
    this.points = [];
    this.ctx.requestRender();
    if (simplified.length < 2) return; // EC-TL-8
    const doc = this.ctx.store.doc;
    const entity: PolylineEntity = {
      id: newId(),
      type: "polyline",
      layerId: this.ctx.activeLayerId(),
      locked: false,
      hidden: false,
      points: simplified,
      closed: false,
      style: structuredClone(doc.defaultStyle)
    };
    this.ctx.store.dispatch({
      kind: "addEntities",
      entities: [entity],
      z: [{ layerId: entity.layerId, index: -1 }]
    });
  }

  override renderDynamic(v: RenderView): void {
    if (this.points.length < 2) return;
    const { ctx } = v;
    ctx.strokeStyle = this.ctx.store.doc.defaultStyle.strokeColor;
    ctx.lineWidth = strokeWidthWorld(this.ctx.store.doc.defaultStyle, v.cam, v.dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(this.points[0]!.x, this.points[0]!.y);
    for (let i = 1; i < this.points.length; i++) ctx.lineTo(this.points[i]!.x, this.points[i]!.y);
    ctx.stroke();
  }
}

export class TapeTool extends BaseTool {
  readonly id = "tape" as const;
  private a: Vec | null = null;
  private cursor: Vec | null = null;

  override activate(): void {
    this.a = null;
    this.ctx.setMeasure("none", "");
    this.ctx.setHint("Tape: click two points to measure · hold Ctrl to drop a guide line");
  }

  override cancel(): void {
    this.a = null;
    this.ctx.setSnapBase(null);
    this.ctx.setCursorTip(null);
  }

  override inProgress(): boolean {
    return this.a !== null;
  }

  override pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    if (!this.a) {
      this.a = e.point;
      this.ctx.setSnapBase(e.point);
      this.ctx.setCursorTip("second point · Ctrl = guide");
      return;
    }
    // ANN-7: Ctrl → drop a guide through the two points (Shift = axis-aligned)
    if (e.ctrl) {
      let dir = { x: e.point.x - this.a.x, y: e.point.y - this.a.y };
      if (e.shift) dir = Math.abs(dir.x) >= Math.abs(dir.y) ? { x: 1, y: 0 } : { x: 0, y: 1 };
      this.ctx.addGuide(this.a, dir);
      this.ctx.setHint("Guide added — snap to it while drawing · clear via right-click on empty canvas");
      this.a = null;
      this.ctx.setSnapBase(null);
      this.ctx.setCursorTip(null);
      this.ctx.requestRender();
      return;
    }
    const { units, precision } = this.ctx.docUnits();
    this.ctx.setHint(
      `Tape: ${formatLength(dist(this.a, e.point), units, precision)} — click to measure again · Ctrl = guide`
    );
    this.a = null; // ANN-7: readout pinned in the status bar until the next action
    this.ctx.setSnapBase(null);
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  override pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    this.ctx.requestRender();
  }

  override key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape" && this.a) {
      this.cancel();
      this.activate();
      return true;
    }
    return false;
  }

  override renderDynamic(v: RenderView): void {
    if (!this.a || !this.cursor) return;
    const { ctx } = v;
    ctx.strokeStyle = "#e0b34d";
    ctx.lineWidth = 1 / v.cam.scale;
    ctx.setLineDash([4 / v.cam.scale, 3 / v.cam.scale]);
    ctx.beginPath();
    ctx.moveTo(this.a.x, this.a.y);
    ctx.lineTo(this.cursor.x, this.cursor.y);
    ctx.stroke();
    ctx.setLineDash([]);
    const { units, precision } = this.ctx.docUnits();
    drawReadout(v, this.cursor, formatLength(dist(this.a, this.cursor), units, precision));
  }
}
